import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import {
  autocompleteTheme,
  datatableTheme,
  buttonTheme,
  toggleswitchTheme,
  selectTheme,
  multiselectTheme,
  selectbuttonTheme,
  togglebuttonTheme,
  inputTextTheme,
  textareaTheme,
  checkboxTheme,
  tabsTheme,
  cardTheme,
  paginatorTheme,
  menuTheme,
  fieldsetTheme,
  badgeTheme,
  tagTheme,
  messageTheme,
  accordionTheme,
  breadcrumbTheme,
  popoverTheme,
  passwordTheme,
  floatLabelTheme,
  treeTheme,
  radiobuttonTheme,
  dividerTheme,
  panelTheme,
  progressbarTheme,
  progressspinnerTheme,
  skeletonTheme,
  scrollpanelTheme,
  tooltipTheme
} from './components';

const MoPreset = definePreset(Aura, {
  primitive: {
    // The palettes are the bottom of the token chain, so they hold literal hex by design:
    // they mirror src/styles/bootstrap/figma.scss (the source of truth), and TypeScript
    // cannot read SCSS. The styleguide checks every mirrored shade against figma.scss and
    // reports drift. There is no cyan palette: info uses the primary colour (MOG-4595).
    // Grayscale — Zink palette (Figma PrimeOne-4.0.0_custom)
    gray: {
      0: '#ffffff',
      50: '#fafafa',
      100: '#f4f4f5',
      200: '#e4e4e7',
      300: '#d4d4d8',
      400: '#a1a1aa',
      500: '#71717a',
      600: '#52525b',
      700: '#3f3f46',
      800: '#27272a',
      900: '#18181b',
      950: '#09090b'
    },
    // Primary Blue — MoBlue
    blue: {
      50: '#e2f7fd',
      100: '#ccf0fb',
      200: '#aae8f9',
      300: '#71d9f6',
      400: '#38c7f2',
      500: '#00bcef',
      600: '#0092b9',
      700: '#006884',
      800: '#00495d',
      900: '#003a4a',
      950: '#002a35'
    },
    // Success / Green (light-mode base)
    green: {
      50: '#f2f9eb',
      100: '#ddedd2',
      200: '#bbdba4',
      300: '#98c876',
      400: '#76b649',
      500: '#55a51c',
      600: '#419114',
      700: '#2d7d00',
      800: '#256700',
      900: '#1d5100',
      950: '#0f2a00'
    },
    // Danger / Red (light-mode base)
    red: {
      50: '#fbeae9',
      100: '#f5ebed',
      200: '#e4afad',
      300: '#d68784',
      400: '#c95f5c',
      500: '#ae0f0a',
      600: '#910c08',
      700: '#740a07',
      800: '#5a0806',
      900: '#3f0504',
      950: '#200302'
    },
    // Warning / Orange (light-mode base)
    orange: {
      50: '#fef4e9',
      100: '#fbe2cd',
      200: '#f9d4b5',
      300: '#f5b67e',
      400: '#f29446',
      500: '#ee7203',
      600: '#c85f03',
      700: '#a24c02',
      800: '#7c3902',
      900: '#562601',
      950: '#2b1301'
    },
    // Help / Purple
    purple: {
      50: '#efe9f3',
      100: '#dfd3e6',
      200: '#cfbdd9',
      300: '#b097c1',
      400: '#9078a9',
      500: '#8060a0',
      600: '#5c2483',
      700: '#4e1f70',
      800: '#3d195a',
      900: '#2b1240',
      950: '#1a0c27'
    },
    // MoPink — brand accent
    pink: {
      50: '#ffffff',
      100: '#fbe6fa',
      200: '#f4b4f2',
      300: '#ed83e9',
      400: '#e651e0',
      500: '#e020d8',
      600: '#ae18a7',
      700: '#7c1178',
      800: '#4a0a47',
      900: '#180318',
      950: '#000000'
    }
  },
  semantic: {
    transitionDuration: '0.2s',
    disabledOpacity: '0.6',
    primary: {
      50: '{blue.50}',
      100: '{blue.100}',
      200: '{blue.200}',
      300: '{blue.300}',
      400: '{blue.400}',
      500: '{blue.500}',
      600: '{blue.600}',
      700: '{blue.700}',
      800: '{blue.800}',
      900: '{blue.900}',
      950: '{blue.950}'
    },
    colorScheme: {
      light: {
        surface: {
          0: '{gray.0}',
          50: '{gray.50}',
          100: '{gray.100}',
          200: '{gray.200}',
          300: '{gray.300}',
          400: '{gray.400}',
          500: '{gray.500}',
          600: '{gray.600}',
          700: '{gray.700}',
          800: '{gray.800}',
          900: '{gray.900}',
          950: '{gray.950}'
        },
        primary: {
          color: '{primary.500}',
          contrastColor: '{gray.0}',
          hoverColor: '{primary.600}',
          activeColor: '{primary.700}'
        },
        highlight: {
          background: '{gray.300}',
          focusBackground: '{gray.400}',
          color: '{gray.900}',
          focusColor: '{gray.900}'
        },
        mask: {
          background: 'rgba(0, 0, 0, 0.4)',
          color: '{surface.200}'
        },
        formField: {
          background: 'transparent',
          disabledBackground: 'transparent',
          filledBackground: '{surface.50}',
          filledHoverBackground: '{surface.50}',
          filledFocusBackground: '{surface.50}',
          borderColor: '{gray.400}',
          hoverBorderColor: '{gray.600}',
          focusBorderColor: '{primary.color}',
          invalidBorderColor: '{red.500}',
          color: '{gray.900}',
          disabledColor: '{gray.400}',
          placeholderColor: '{gray.500}',
          floatLabelColor: '{gray.600}',
          floatLabelFocusColor: '{primary.600}',
          floatLabelActiveColor: '{gray.600}',
          floatLabelInvalidColor: '{red.500}',
          iconColor: '{gray.500}',
          shadow: 'none'
        },
        text: {
          color: '{gray.900}',
          hoverColor: '{gray.800}',
          mutedColor: '{gray.500}',
          hoverMutedColor: '{gray.600}'
        },
        content: {
          background: '{surface.0}',
          hoverBackground: '{gray.200}',
          borderColor: '{gray.200}',
          color: '{text.color}',
          hoverColor: '{text.hover.color}'
        },
        overlay: {
          select: {
            background: '{surface.0}',
            borderColor: '{gray.200}',
            color: '{text.color}'
          },
          popover: {
            background: '{surface.0}',
            borderColor: '{gray.200}',
            color: '{text.color}'
          },
          modal: {
            background: '{surface.0}',
            borderColor: '{gray.400}',
            color: '{text.color}'
          }
        },
        list: {
          option: {
            focusBackground: '{gray.100}',
            selectedBackground: '{gray.300}',
            selectedFocusBackground: '{gray.400}',
            color: '{text.color}',
            focusColor: '{text.hover.color}',
            selectedColor: '{gray.900}',
            selectedFocusColor: '{gray.900}',
            icon: {
              color: '{gray.600}',
              focusColor: '{gray.700}'
            }
          },
          optionGroup: {
            background: 'transparent',
            color: '{gray.600}',
            fontWeight: '700'
          }
        },
        navigation: {
          item: {
            focusBackground: '{gray.100}',
            activeBackground: '{gray.200}',
            color: '{text.color}',
            focusColor: '{text.hover.color}',
            activeColor: '{text.hover.color}',
            icon: {
              color: '{gray.600}',
              focusColor: '{gray.700}',
              activeColor: '{gray.700}'
            }
          },
          submenuLabel: {
            background: 'transparent',
            color: '{gray.600}',
            fontWeight: '700'
          },
          submenuIcon: {
            color: '{gray.600}',
            focusColor: '{gray.700}',
            activeColor: '{gray.700}'
          }
        }
      },
      dark: {
        surface: {
          0: '{gray.800}',
          50: '{gray.800}',
          100: '{gray.700}',
          200: '{gray.600}',
          300: '{gray.500}',
          400: '{gray.400}',
          500: '{gray.300}',
          600: '{gray.200}',
          700: '{gray.100}',
          800: '{gray.50}',
          900: '{gray.0}',
          950: '{gray.0}'
        },
        primary: {
          color: '{primary.500}',
          contrastColor: '{gray.0}',
          hoverColor: '{primary.400}',
          activeColor: '{primary.300}'
        },
        highlight: {
          background: '{gray.600}',
          focusBackground: '{gray.500}',
          color: '{gray.0}',
          focusColor: '{gray.0}'
        },
        mask: {
          background: 'rgba(0, 0, 0, 0.6)',
          color: '{surface.200}'
        },
        formField: {
          background: 'transparent',
          disabledBackground: 'transparent',
          filledBackground: '{gray.800}',
          filledHoverBackground: '{gray.800}',
          filledFocusBackground: '{gray.800}',
          borderColor: '{gray.500}',
          hoverBorderColor: '{gray.400}',
          focusBorderColor: '{primary.color}',
          invalidBorderColor: '{red.600}',
          color: '{gray.0}',
          disabledColor: '{gray.500}',
          placeholderColor: '{gray.500}',
          floatLabelColor: '{gray.400}',
          floatLabelFocusColor: '{primary.400}',
          floatLabelActiveColor: '{gray.400}',
          floatLabelInvalidColor: '{red.600}',
          iconColor: '{gray.400}'
        },
        text: {
          color: '{gray.0}',
          hoverColor: '{gray.200}',
          mutedColor: '{gray.500}',
          hoverMutedColor: '{gray.400}'
        },
        content: {
          background: '{gray.800}',
          hoverBackground: '{gray.700}',
          borderColor: '{gray.700}',
          color: '{text.color}',
          hoverColor: '{text.hover.color}'
        },
        overlay: {
          select: {
            background: '{gray.800}',
            borderColor: '{gray.700}',
            color: '{text.color}'
          },
          popover: {
            background: '{gray.800}',
            borderColor: '{gray.700}',
            color: '{text.color}'
          },
          modal: {
            background: '{gray.800}',
            borderColor: '{gray.600}',
            color: '{text.color}'
          }
        },
        list: {
          option: {
            focusBackground: '{gray.700}',
            selectedBackground: '{gray.600}',
            selectedFocusBackground: '{gray.500}',
            color: '{text.color}',
            focusColor: '{text.hover.color}',
            selectedColor: '{gray.0}',
            selectedFocusColor: '{gray.0}',
            icon: {
              color: '{gray.400}',
              focusColor: '{gray.300}'
            }
          }
        },
        navigation: {
          item: {
            focusBackground: '{gray.700}',
            activeBackground: '{gray.600}',
            color: '{text.color}',
            focusColor: '{text.hover.color}',
            activeColor: '{text.hover.color}',
            icon: {
              color: '{gray.400}',
              focusColor: '{gray.300}',
              activeColor: '{gray.300}'
            }
          },
          submenuLabel: {
            background: 'transparent',
            color: '{gray.400}',
            fontWeight: '700'
          },
          submenuIcon: {
            color: '{gray.400}',
            focusColor: '{gray.300}',
            activeColor: '{gray.300}'
          }
        }
      }
    }
  },
  // Used without an entry here, on purpose: inputnumber, iconfield, dialog, confirmdialog,
  // avatar, chip, datepicker. Aura only reads tokens this preset already sets for them.
  // The reasons live in INTENTIONAL_AURA_DEFAULT in styleguide/lib/extract-theme.mjs.
  components: {
    autocomplete: autocompleteTheme,
    datatable: datatableTheme,
    accordion: accordionTheme,
    button: buttonTheme,
    toggleswitch: toggleswitchTheme,
    select: selectTheme,
    multiselect: multiselectTheme,
    selectbutton: selectbuttonTheme,
    togglebutton: togglebuttonTheme,
    inputtext: inputTextTheme,
    textarea: textareaTheme,
    checkbox: checkboxTheme,
    tabs: tabsTheme,
    card: cardTheme,
    paginator: paginatorTheme,
    menu: menuTheme,
    fieldset: fieldsetTheme,
    badge: badgeTheme,
    tag: tagTheme,
    message: messageTheme,
    breadcrumb: breadcrumbTheme,
    popover: popoverTheme,
    password: passwordTheme,
    floatlabel: floatLabelTheme,
    tree: treeTheme,
    radiobutton: radiobuttonTheme,
    divider: dividerTheme,
    panel: panelTheme,
    progressbar: progressbarTheme,
    progressspinner: progressspinnerTheme,
    skeleton: skeletonTheme,
    scrollpanel: scrollpanelTheme,
    tooltip: tooltipTheme
  }
  // NOTE: CSS variables are now defined in src/styles/bootstrap/variables.scss
  // This provides them at compile-time for Bootstrap SCSS compilation
  // figma.scss is the single source of truth for all color values
});

export default MoPreset;
